import { apiPost, apiPut, apiGet } from "./apiService";


export interface ScriptActionPayload {
  [key: string]: unknown;
}

export interface SmallDataFixAudit {
  auditId: number;
  smallDataFixMasterId: number;
  formData: string;
  environment: string;
  executedBy: string;
  executedOn: string;
}

export interface IncidentFormDataResponse {
  id?: number;
  snowIncidentNumber?: string;
  requestorEmail?: string;
  spName?: string;
  isPublishedToProd?: boolean;
  isApproved?: boolean;
  smallDataFixAudits?: SmallDataFixAudit[];
  formData: string;
}

const postAction = async <T = unknown>(
  endpoint: string,
  payload: ScriptActionPayload
): Promise<T> => {
  const response = await apiPost<T>(endpoint, payload);
  return response.data;
};

const putAction = async <T = unknown>(
  endpoint: string,
  payload: ScriptActionPayload
): Promise<T> => {
  const response = await apiPut<T>(endpoint, payload);
  return response.data;
};

const getAction = async <T = unknown>(
  endpoint: string,
  params: ScriptActionPayload
): Promise<T> => {
  const response = await apiGet<T>(endpoint, params);
  return response.data;
};

const getActionByPath = async <T = unknown>(
  endpoint: string
): Promise<T> => {
  const response = await apiGet<T>(endpoint);
  return response.data;
};

export const PageScriptActionsService = {
  invoicePreview<T = unknown>(payload: ScriptActionPayload): Promise<T> {
    return getAction<T>("PayInvoiceFromSuspense/Preview", payload);
  },

  approveSript<T = unknown>(payload: ScriptActionPayload): Promise<T> {
    return putAction<T>("SmallDataFix/UpdateApproval", payload);
  },

  invoiceExecute<T = unknown>(payload: ScriptActionPayload): Promise<T> {
    return postAction<T>("PayInvoiceFromSuspense/Execute", payload);
  },

  claimNumberUpdatePreview<T = unknown>(
    payload: ScriptActionPayload
  ): Promise<T> {
    return postAction<T>("RenameClaimNumber/preview", payload);
  },

  fetchRequesterEmailByIncidentId<T = unknown>(
    incidentId: string
  ): Promise<T> {
    return getActionByPath<T>(
      `ServiceNow/details/${encodeURIComponent(incidentId)}`
    );
  },

  claimNumberUpdateExecute<T = unknown>(
    payload: ScriptActionPayload
  ): Promise<T> {
    return postAction<T>("RenameClaimNumber/Execute", payload);
  },

  claimCoveragePreview<T = unknown>(
    payload: ScriptActionPayload
  ): Promise<T> {
    return postAction<T>("claims-coverage-update/preview", payload);
  },

  claimCoverageExecute<T = unknown>(
    payload: ScriptActionPayload
  ): Promise<T> {
    return postAction<T>("claims-coverage-update/execute", payload);
  },

  claimCoverageWithAuditPreview<T = unknown>(
    payload: ScriptActionPayload
  ): Promise<T> {
    return postAction<T>(
      "claims-coverage-update-with-audit/preview",
      payload
    );
  },

  claimCoverageWithAuditExecute<T = unknown>(
    payload: ScriptActionPayload
  ): Promise<T> {
    return postAction<T>(
      "claims-coverage-update-with-audit/execute",
      payload
    );
  },

  GetPrePreviewByIncident<T = unknown>(
    id: string,
    latestOnly = false,
    isPaymentPortalExists = false
  ): Promise<T> {
    return getActionByPath<T>(
      `SmallDataFix/getmasterdata/${encodeURIComponent(
        id
      )}/${latestOnly}/${isPaymentPortalExists}`
    );
  },

  GetEmailByIncident<T = unknown>(incident: string): Promise<T> {
    return getActionByPath<T>(
      `ServiceNow/details/${encodeURIComponent(incident)}`
    );
  },

  SmallDataFixCreate<T = unknown>(
    payload: ScriptActionPayload
  ): Promise<T> {
    return postAction<T>("SmallDataFix/create", payload);
  },

  claimCoverageLimitPreview<T = unknown>(
    payload: ScriptActionPayload
  ): Promise<T> {
    return postAction<T>("UpdateClaimCovsLimits/Preview", payload);
  },

  restoreClaimCoverageFromHistoryPreview<T = unknown>(
    payload: ScriptActionPayload
  ): Promise<T> {
    return postAction<T>(
      "RestoreClaimCovsFromHistory/Preview",
      payload
    );
  },

  uwLimitPreview<T = unknown>(
    payload: ScriptActionPayload
  ): Promise<T> {
    return postAction<T>("UpdateUWLimits/Preview", payload);
  },

  claimCoverageLimitExecute<T = unknown>(
    payload: ScriptActionPayload
  ): Promise<T> {
    return postAction<T>("UpdateClaimCovsLimits/Execute", payload);
  },

  restoreClaimCoverageFromHistoryExecute<T = unknown>(
    payload: ScriptActionPayload
  ): Promise<T> {
    return postAction<T>(
      "RestoreClaimCovsFromHistory/Execute",
      payload
    );
  },

  uwLimitExecute<T = unknown>(
    payload: ScriptActionPayload
  ): Promise<T> {
    return postAction<T>("UpdateUWLimits/Execute", payload);
  },

  parentAgencyPreview<T = unknown>(
    payload: ScriptActionPayload
  ): Promise<T> {
    return postAction<T>("Agency/Preview", payload);
  },

  parentAgencyExecute<T = unknown>(
    payload: ScriptActionPayload
  ): Promise<T> {
    return postAction<T>("Agency/Execute", payload);
  },

  fetchIncidentFormData(
    incidentId: string,
    isPaymentPortalExists = false
  ): Promise<IncidentFormDataResponse> {
    return getActionByPath<IncidentFormDataResponse>(
      `SmallDataFix/getmasterdata/${encodeURIComponent(
        incidentId
      )}/true/${isPaymentPortalExists}`
    );
  },

  insertUnderwriterPreview<T = unknown>(
    payload: ScriptActionPayload
  ): Promise<T> {
    return getAction<T>("Underwriter/Preview", payload);
  },

  insertUnderwriterExecute<T = unknown>(
    payload: ScriptActionPayload
  ): Promise<T> {
    return postAction<T>("Underwriter/addusers", payload);
  }
};
